import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { CircularProgress, DialogContent } from '@mui/material';
import { darken } from 'polished';
import styled, { useTheme } from 'styled-components';

import { useResources } from '@/contexts/resource-context';
import { createSaml2Tenant, getSaml2Settings } from '@/features/canvas/api/tenant-sso';
import Button from '@/features/ui/components/Button/Button';
import { Dialog } from '@/features/ui/components/Dialog';
import { Form, useFormContext } from '@/features/ui/components/Form';
import { FormSelectInput } from '@/features/ui/components/FormSelectInput';
import { FormTextInput } from '@/features/ui/components/FormTextInput';

const InnerForm = ({
  data,
}: {
  data:
    | {
        acs: string;
        entityId: string;
        loginUrl: string;
        logoutUrl: string;
        metadata: string;
        saml2LoginUrl: string;
        x509certificate: string;
      }
    | undefined;
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const { logoUrl } = useResources();

  useEffect(() => {
    if (!data) return;

    Object.keys(data).forEach(key => {
      setValue(key, data[key as keyof typeof data]);
    });
    setValue('signature_algorithm', 1);
  }, [data, setValue]);

  return (
    <FormContent>
      {logoUrl && <LogoWrapper src={logoUrl} />}
      <SelectWrapper style={{ margin: '2rem 0' }}>
        <h4>{t('sso_setup.method')}</h4>
        <p>{t('sso_setup.saml2')}</p>
      </SelectWrapper>
      <SelectWrapper style={{ margin: '2rem 0' }}>
        <h4>{t('sso_setup.configuration')}</h4>
        <StyledFormInput name="metadata" readOnly label="Identifier (Entity ID)" copy />
        <StyledFormInput name="acs" readOnly label="Reply URL (ACS)" copy />
      </SelectWrapper>
      <StyledFormInput
        name="x509certificate"
        placeholder="Standard Strength Certificate (2048-bit)"
        label="X509 Certificate:"
        required
        copy
        rules={{ required: { value: true, message: t('form.field_required_validation') } }}
      />
      <SelectWrapper>
        <label htmlFor="signature_algorithm">{t('sso_setup.saml_signature')}</label>
        <StyledFormSelectInput
          name="signature_algorithm"
          value={{ id: 1, label: 'SHA-1' }}
          options={SIGNATURE_ALGORITHMS.map((item, key) => ({ id: key + 1, label: item }))}
          required
          fullWidth
          variant="standard"
          noMargin
          rules={{ required: { value: true, message: t('form.field_required_validation') } }}
        />
      </SelectWrapper>
      <StyledFormInput
        name="entityId"
        placeholder="Issuer URL"
        label="Issuer URL"
        required
        copy
        rules={{ required: { value: true, message: t('form.field_required_validation') } }}
      />
      <StyledFormInput
        name="loginUrl"
        placeholder="SAML 2.0 Endpoint (HTTP)"
        label="SAML 2.0 Endpoint (HTTP)"
        required
        copy
        rules={{ required: { value: true, message: t('form.field_required_validation') } }}
      />
      <StyledFormInput
        name="logoutUrl"
        placeholder="SLO Endpoint (HTTP)"
        label="SLO Endpoint (HTTP)"
        required
        copy
        rules={{ required: { value: true, message: t('form.field_required_validation') } }}
      />
      <ButtonWrappers>
        <StyledButton type="submit" variant="action" label={t('sso_setup.save')} />
        <StyledButton
          type="button"
          variant="secondary"
          label="Test Single sign-on"
          onClick={() => {
            if (data?.saml2LoginUrl) {
              window.open(data.saml2LoginUrl, '_blank');
            }
          }}
        />
      </ButtonWrappers>
    </FormContent>
  );
};

const SIGNATURE_ALGORITHMS = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'];

interface ICreateSaml2Tenant {
  entityId: string;
  loginUrl: string;
  logoutUrl: string;
  x509certificate: string;
}

const SsoAdminSetup = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data, isLoading } = useQuery(getSaml2Settings.name, () => getSaml2Settings());
  const [ssoConfiguration, setSsoConfiguration] = useState<{
    acs: string;
    loginUrl: string;
    logoutUrl: string;
    metadata: string;
    sls: string;
  } | null>();

  const { mutateAsync } = useMutation(
    ({ entityId, loginUrl, logoutUrl, x509certificate }: ICreateSaml2Tenant) =>
      createSaml2Tenant(entityId, loginUrl, logoutUrl, x509certificate),
    { onSuccess: setSsoConfiguration }
  );

  const onSubmit = async (formData: FieldValues) => {
    const samlSettingsData = formData as unknown as ICreateSaml2Tenant;

    await mutateAsync({ ...samlSettingsData });
  };

  if (isLoading) {
    return (
      <LoaderWrapper>
        <StyledLoader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <PageWrapper>
        <Header>
          <h2>{t('sso_setup.title')}</h2>
        </Header>
        <StyledForm onSubmit={onSubmit}>
          <InnerForm data={data} />
        </StyledForm>
      </PageWrapper>
      <Dialog open={!!ssoConfiguration} onClose={() => setSsoConfiguration(null)}>
        <DialogContent
          sx={{
            color: theme.palette.brand.textPrimary,
            padding: '0 2rem',
          }}
        >
          <Title>{t('sso_setup.configuration')}</Title>
          <Form>
            <DialogFormWrapper>
              <StyledFormInput
                name="dialog_metadata"
                label="SSO Metadata"
                readOnly
                value={ssoConfiguration?.metadata}
              />
              <StyledFormInput
                name="dialog_acs"
                label="SSO ACS"
                readOnly
                value={ssoConfiguration?.acs}
              />
              <StyledFormInput
                name="dialog_sls"
                label="SSO SLS"
                readOnly
                value={ssoConfiguration?.sls}
              />
              <StyledFormInput
                name="dialog_loginUrl"
                label="SSO Login Url"
                readOnly
                value={ssoConfiguration?.loginUrl}
              />
              <StyledFormInput
                name="dialog_logoutUrl"
                label="SSO Logout Url"
                readOnly
                value={ssoConfiguration?.logoutUrl}
              />
            </DialogFormWrapper>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ButtonWrappers = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DialogFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.palette.brand.primary};
  font-size: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${({ theme }) => theme.palette.brand.backgroundInputFocus};
  margin-bottom: 2rem;
`;

const LoaderWrapper = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoader = styled(CircularProgress)`
  color: '#6767e0';
`;

const StyledButton = styled(Button)`
  border-radius: 4rem;
  padding: 0.5rem 3rem 0.5rem 3rem;
  font-weight: 500;
  font-size: 2rem;
  text-transform: none;
  max-width: max-content;
  margin-top: 4rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 21px;

  label,
  h4 {
    font-size: 2rem;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.brand.primary};
  }

  p {
    font-size: 2rem;
    font-style: italic;
    color: ${({ theme }) => theme.palette.brand.textPrimary};
  }
`;

const StyledFormSelectInput = styled(FormSelectInput)`
  width: 100%;
  border: 2px solid ${({ theme }) => theme.palette.brand.corePPTCopy};

  &:focus-within {
    border: 2px solid ${({ theme }) => darken(0.1, theme.palette.brand.corePPTCopy)};
    background: ${({ theme }) => theme.palette.brand.white};
  }
`;

const StyledFormInput = styled(FormTextInput)`
  padding: 0.5rem 1.5rem;
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.palette.brand.corePPTCopy};
  font-size: 2rem;
  font-style: italic;

  &:focus-within {
    border: 2px solid ${({ theme }) => darken(0.1, theme.palette.brand.corePPTCopy)};
    background: ${({ theme }) => theme.palette.brand.white};
  }
`;

const LogoWrapper = styled.img`
  height: auto;
  width: auto;
  cursor: pointer;
  display: block;
  max-height: 40px;
  width: max-content;
  object-fit: contain;
  margin-bottom: 2rem;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5rem 10rem;
  width: 100%;
  gap: 3rem;
`;

const StyledForm = styled(Form)`
  background-color: ${({ theme }) => theme.palette.background.default};
  display: flex;
  flex-grow: 1;
  width: 100%;
  max-width: 900px;
  margin: 2rem 0;
  border-radius: 10px;
  justify-content: center;
`;

const PageWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.palette.brand.backgroundMain};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  width: 100vw;
  background-color: ${({ theme }) => darken(0.1, theme.palette.brand.primary)};
  padding: 2rem 2rem;
  height: max-content;

  h2 {
    font-size: 2rem;
    color: ${({ theme }) => theme.palette.brand.white};
    font-weight: 500;
  }
`;

export default SsoAdminSetup;
